import TagManager from '@sooro-io/react-gtm-module';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/store';
import { JOURNEY_TYPES } from '@/constants/constants';
import { processItems, buildDataLayerEvent, GtmMoneybuyItem } from './helpers';
import { GTM_EVENTS, GtmEventChangeInterface } from '@/services/gtm/gtmSlice';

const { SINGLE } = JOURNEY_TYPES;

function HandleGtmEvents(currentState: RootState, action: PayloadAction<GtmEventChangeInterface>) {
  const { eventType: actionName, cartId, cartData } = action.payload;

  // Grab various bits of state we need:
  const {
    currentMoneybuyPosition: selectedPosition,
    amount: selectedAmount,
    provider: thisProvider,
    giftaid: giftAidChoice,
    currency
  } = currentState.paymentSelect;

  const { previousUserChoice } = currentState.gtm;

  const thisJourneyName = currentState.journeys.journey;
  const transactionState = currentState.transaction;
  const referringUrl = currentState.urlOverrides.siteUrlOverride;

  // State conditions required before running the main code:
  const validConfig = thisJourneyName && cartId && selectedPosition !== null;

  // Use the current journey to determine which moneybuy data we're interested in
  const thisJourneysMoneybuys = (thisJourneyName === SINGLE)
    ? cartData?.journeys.single?.moneybuys
    : cartData?.journeys.monthly?.moneybuys;

  // Check we have what we need first:
  if (validConfig && thisJourneysMoneybuys) {
    // Variables to store our processed stuff:
    let processedItems: GtmMoneybuyItem[] | undefined;
    const theseDataLayerEventNames: string[] = [];
    let selectedItem = null;
    let removeFromCart = false;
    let removeEvent;

    // Use either the moneybuy the user has selected via the supplied
    // 'position' index, or create one to represent the 'manual entry'.
    //
    // We only REALLY need to handmake a manual entry 'moneybuy' so it
    // can slot into existing functions as a data-models 'Moneybuy';
    // 'description' isn't used, we just need to match the object shape:
    selectedItem = selectedPosition === -1
      ? [{ amount: selectedAmount, description: 'MANUAL INPUT' }]
      : [thisJourneysMoneybuys[selectedPosition]];

    // The switch statement lets us handle different data
    // differently, based on the context that triggered it:
    switch (actionName) {
      /**
       * Triggered in 'JourneySelect' and 'useJourneyOptions' respectively,
       * grouped as they pushing the same DataLayer event:
      */
      case GTM_EVENTS.FRONT_PAGE_LOAD:
      case GTM_EVENTS.FRONT_PAGE_CHANGE:
        theseDataLayerEventNames.push('view_item_list');

        // Use ALL the moneybuys that the user is currently being shown:
        processedItems = processItems({
          theseMoneybuys: thisJourneysMoneybuys,
          thisJourneyName,
          cartId,
          referringUrl
        });
        break;

      /**
       * Triggered in 'useJourneyStartNavigate'
      */
      case GTM_EVENTS.GIFTAID_PAGE:
        // Multiple DataLayer events for this scenario:
        theseDataLayerEventNames.push('view_item', 'select_item', 'begin_checkout', 'add_to_cart');

        // If the user has _previously_ made a choice on this journey (firing
        // an "add_to_cart" event), set up an event here to 'remove' it,
        // grabbing the previously-stored and the current values to compare them:
        if (selectedItem && previousUserChoice) {
          const { previousAmount, previousJourney } = previousUserChoice;
          const { amount: currentAmount } = selectedItem[0];

          // If there HAS been a change to either the amount or journey type, update
          // the flag accordingly to create the 'remove_from_cart' DL event later:
          removeFromCart = previousAmount !== currentAmount || previousJourney !== thisJourneyName;
        }

        // Process only the _selected_ MB:
        processedItems = processItems({
          theseMoneybuys: selectedItem,
          thisJourneyName,
          cartId,
          position: selectedPosition,
          amount: selectedAmount,
          referringUrl
        });
        break;

      /**
       * Triggered in 'usePaymentSuccess'
      */
      case GTM_EVENTS.AFTER_PAYMENT_PAGE:
        theseDataLayerEventNames.push('add_payment_info', 'add_shipping_info', 'purchase');
        // Process only the _selected_ MB, this time passing in the now-set GiftAid choice:
        processedItems = processItems({
          theseMoneybuys: selectedItem, thisJourneyName, cartId, position: selectedPosition, amount: selectedAmount, giftAid: giftAidChoice, referringUrl
        });
        break;

      /**
       * And just in case, stop things going any further if it's a different event we don't care about:
      */
      default:
        console.error('*** Unknown GTM event:', actionName);
        return;
    }

    /*
     * Now we've picked and processed the appropriate data for each of our custom
     * GTM events, ensure we've got what we need first before we try and use it:
    */
    if (processedItems) {
      // Works as a flag within the proceeding buildDataLayerEvent call - we only want to pass a SINGLE
      // value there (as the user-selected amount set in GIFTAID_PAGE and AFTER_PAYMENT_PAGE), rather
      // than EVERY moneybuy value the user is shown in FRONT_PAGE_LOAD and FRONT_PAGE_CHANGE.
      //
      // By checking the length of our processedItems to differentiate, we can ensure we're only passing
      // the correct, single, user-chosen value.
      const thisValue = processedItems.length === 1 ? Number(processedItems[0].price) : undefined;

      const thisTransactionId = transactionState.transactionId || transactionState.subscriptionId;

      // Produces a new object to submit for each named DataLayer event, and
      // adds it to our array, passing in the 'value' and 'provider' when available:
      const everyDataLayerEvent = theseDataLayerEventNames.map(eventName => buildDataLayerEvent(eventName, processedItems, currency, thisValue, thisProvider, thisTransactionId));

      // Creates a separate remove_from_cart event, using an object based on the cached 'previous' moneybuy item details:
      if (removeFromCart && previousUserChoice) {
        // Grab our previously-selected moneybuy config, and create a new MB item:
        const { previousAmount, previousJourney, previousPosition } = previousUserChoice;
        const processedRemovedItem = processItems({
          theseMoneybuys: selectedItem,
          thisJourneyName: previousJourney,
          cartId,
          position: previousPosition,
          amount: previousAmount,
          referringUrl
        });
        // Build our removal event with it:
        removeEvent = buildDataLayerEvent('remove_from_cart', processedRemovedItem, currency, previousAmount, thisProvider);
      }

      // Iterate over this array to fire the DataLayer events:
      everyDataLayerEvent.forEach((thisDataLayerEvent) => {
        // Required before EVERY DL event as a means of reset:
        TagManager.dataLayer({
          dataLayer: {
            ecommerce: null,
            site: [{
              category: 'cr-donation',
              pageCategory: 'cr_donate',
              cartID: cartId,
            }],
          }
        });

        // Fire this event
        TagManager.dataLayer({
          dataLayer: thisDataLayerEvent
        });
      });

      // And FINALLY, IF we produced a remove_from_cart event, fire it off here:
      if (removeEvent) {
        // Again, required before every DL event
        TagManager.dataLayer({
          dataLayer: {
            ecommerce: null,
            site: [{
              category: 'cr-donation',
              pageCategory: 'cr_donate',
              cartID: cartId,
            }],
          }
        });

        TagManager.dataLayer({
          dataLayer: removeEvent
        });
      }
    }
  }
}

export { HandleGtmEvents };
